import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { collection, collectionGroup, getCountFromServer, query, where } from 'firebase/firestore';
import { auth, db, functions } from '../lib/firebase';

/**
 * DeleteAccountModal — user-facing self-deletion entry point
 * (ADMIN_SPEC §13). Triggered from the AccountPopover "delete my
 * account" row.
 *
 * Three layers of safeguard before the destructive call fires:
 *   1. Plain-language explanation of what happens.
 *   2. Pre-flight counts so the user sees what they're losing.
 *   3. Type-the-word confirmation: literal "delete" or "DELETE"
 *      typed into a text field. Confirm button stays disabled
 *      until the input matches (case-insensitive).
 *
 * On confirm:
 *   - Calls `selfSuspendAccount` Cloud Function (suspends + emails
 *     recovery link).
 *   - Signs the user out client-side.
 *   - Navigates to /goodbye for the post-suspend message.
 *
 * Pre-flight counts are computed via Firestore aggregate-count
 * queries so we don't pull every doc just to count it. Cheap.
 */
export default function DeleteAccountModal({ open, onClose, user }) {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null); // { boards, lists, reminders }
  const [confirmText, setConfirmText] = useState('');
  const [pending, setPending] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setConfirmText('');
    setErrorMsg(null);
    setPending(false);
    setCounts(null);
  }, [open]);

  // Compute pre-flight counts when the modal opens. Aggregate count
  // is a single round-trip per collection — avoids reading every
  // doc just to render "47 boards".
  useEffect(() => {
    if (!open || !user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const boardsCol = collection(db, 'users', user.uid, 'boards');
        const remindersCol = collection(db, 'users', user.uid, 'reminders');
        // For lists we need a collectionGroup query scoped to this user.
        // Lists live at users/{uid}/boards/{boardId}/lists, so a plain
        // collectionGroup over 'lists' returns docs across all users.
        // Filter by ownerId — but lists don't carry an ownerId field.
        // Pragmatic fallback: count boards in parallel + sum lists per
        // board using getCountFromServer. Two reads per board.
        const [boardsAgg, remindersAgg] = await Promise.all([
          getCountFromServer(boardsCol),
          getCountFromServer(remindersCol),
        ]);
        if (cancelled) return;
        const boardCount = boardsAgg.data().count;
        // For lists: pull the first 50 boards + count lists in each.
        // Cheap at our scale; a perfect total isn't required for the
        // pre-flight — order-of-magnitude is what the user wants.
        // A future optimization: keep a denormalized count on the
        // user account doc, updated by a Cloud Function trigger.
        const boardsSnap = await import('firebase/firestore').then(m =>
          m.getDocs(m.query(boardsCol, m.limit(50))),
        );
        if (cancelled) return;
        let listCount = 0;
        for (const b of boardsSnap.docs) {
          const listsAgg = await getCountFromServer(
            collection(db, 'users', user.uid, 'boards', b.id, 'lists'),
          );
          if (cancelled) return;
          listCount += listsAgg.data().count;
        }
        setCounts({
          boards: boardCount,
          lists: listCount,
          reminders: remindersAgg.data().count,
        });
      } catch (err) {
        console.warn('[DeleteAccountModal] count failed', err);
        if (!cancelled) {
          setCounts({ boards: '?', lists: '?', reminders: '?' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.uid]);

  const confirmEnabled = confirmText.trim().toLowerCase() === 'delete';

  const onConfirm = async () => {
    if (!confirmEnabled || pending) return;
    setPending(true);
    setErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'selfSuspendAccount');
      await fn({});
      // Sign out client-side, then bounce to the goodbye page.
      try { await signOut(auth); } catch (_) { /* already disabled, ignore */ }
      // Use window.location so the React app fully boots fresh on
      // /goodbye — no stale auth state lingering. Pass the user's
      // email via query string so the goodbye page can show
      // "recovery link sent to {email}" — a concrete affirmation
      // that the email is on its way.
      const emailParam = user?.email ? `?email=${encodeURIComponent(user.email)}` : '';
      window.location.href = `/goodbye${emailParam}`;
    } catch (err) {
      console.error('[DeleteAccountModal] suspend failed', err);
      setErrorMsg(err?.message || 'could not delete account — try again');
      setPending(false);
    }
  };

  if (!open || !user) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 3500,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.15s ease',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--danger, #d04a4a)',
          borderRadius: 'var(--radius)',
          maxWidth: '480px',
          width: '100%',
          padding: '24px 26px',
          boxShadow: 'var(--shadow-modal-heavy)',
          fontFamily: 'var(--mono)',
        }}
      >
        <h2 style={{
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--danger, #d04a4a)',
          margin: '0 0 12px',
        }}>
          delete your listicity account?
        </h2>

        <p style={{
          fontSize: '12px',
          color: 'var(--text-dim)',
          lineHeight: 1.6,
          margin: '0 0 16px',
        }}>
          this will permanently delete everything you've created on listicity.
          you have <strong style={{ color: 'var(--text)' }}>7 days</strong> to change your mind —
          we'll send a recovery link to your email automatically. after 7 days,
          your data is gone for good.
        </p>

        <div style={{
          padding: '10px 14px',
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          marginBottom: 16,
          fontSize: '12px',
          color: 'var(--text)',
          lineHeight: 1.7,
        }}>
          <strong>what gets deleted:</strong>
          {counts ? (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--text-dim)' }}>
              <li>{counts.boards} board{counts.boards === 1 ? '' : 's'}</li>
              <li>{counts.lists} list{counts.lists === 1 ? '' : 's'}</li>
              <li>{counts.reminders} reminder{counts.reminders === 1 ? '' : 's'}</li>
              <li>your push subscriptions</li>
              <li>your access to anything shared with you</li>
              <li>your sign-in account</li>
            </ul>
          ) : (
            <div style={{ marginTop: 6, color: 'var(--text-dimmer)', fontSize: '11px' }}>
              counting…
            </div>
          )}
        </div>

        <label style={{
          display: 'block',
          fontSize: '12px',
          color: 'var(--text)',
          marginBottom: 6,
        }}>
          type <strong style={{ color: 'var(--danger, #d04a4a)' }}>delete</strong> to confirm:
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="delete"
          autoFocus
          disabled={pending}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            background: 'var(--bg)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: '14px',
            outline: 'none',
            marginBottom: 16,
          }}
        />

        {errorMsg && (
          <div style={{
            padding: '8px 10px',
            marginBottom: 12,
            background: 'var(--surface2)',
            border: '1px solid var(--danger, #d04a4a)',
            borderRadius: 'var(--radius)',
            fontSize: '11px',
            color: 'var(--danger, #d04a4a)',
          }}>
            {errorMsg}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={pending}
            style={{
              background: 'transparent',
              color: 'var(--text-dim)',
              border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)',
              padding: '8px 14px',
              fontFamily: 'var(--mono)',
              fontSize: '12px',
              cursor: pending ? 'wait' : 'pointer',
            }}
          >
            cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!confirmEnabled || pending}
            style={{
              background: confirmEnabled && !pending ? 'var(--danger, #d04a4a)' : 'transparent',
              color: confirmEnabled && !pending ? '#fff' : 'var(--text-dimmer)',
              border: '1px solid ' + (confirmEnabled ? 'var(--danger, #d04a4a)' : 'var(--border)'),
              borderRadius: 'var(--radius)',
              padding: '8px 14px',
              fontFamily: 'var(--mono)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: confirmEnabled && !pending ? 'pointer' : 'not-allowed',
              opacity: confirmEnabled ? 1 : 0.5,
            }}
          >
            {pending ? 'deleting…' : 'delete my account'}
          </button>
        </div>
      </div>
    </div>
  );
}
